import { isMenuFooterSectionKey } from '@atlaskit/editor-common/type-ahead-is-menu-footer-section-key';
import { isSectionOverflowItemKey } from '@atlaskit/editor-common/type-ahead-is-section-overflow-item-key';
import {
	resolveSurface,
	willComponentRender,
} from '@atlaskit/editor-ui-control-model/surface-renderer';
import type { SurfaceIdentifier } from '@atlaskit/editor-ui-control-model/surface-renderer/types';
import type {
	RegisterComponent,
	RegisterMenuItem,
	RegisterMenuSection,
} from '@atlaskit/editor-ui-control-model/types';

export type TypeAheadMenuSection = [RegisterMenuSection, ...RegisterMenuItem[]];

export type TypeAheadMenuModel = {
	footer: RegisterMenuItem | undefined;
	root: RegisterComponent | undefined;
	sections: TypeAheadMenuSection[];
};

export const buildTypeAheadMenuModel = (
	components: RegisterComponent[],
	rootComponent: SurfaceIdentifier,
	sectionOverflowLimit?: number,
): TypeAheadMenuModel => {
	const { root, childrenMap, topLevelChildren } = resolveSurface(components, rootComponent);
	const sections: TypeAheadMenuSection[] = [];
	let footer: RegisterMenuItem | undefined;

	for (const section of topLevelChildren ?? []) {
		if (section.type !== 'menu-section' || !willComponentRender(section, childrenMap)) {
			continue;
		}

		const children = childrenMap.get(section.key) ?? [];
		if (isMenuFooterSectionKey(section.key)) {
			footer = children.find(
				(child): child is RegisterMenuItem =>
					child.type === 'menu-item' && willComponentRender(child, childrenMap),
			);
			continue;
		}

		let viewMoreItem: RegisterMenuItem | undefined;
		for (let index = children.length - 1; index >= 0; index--) {
			const child = children[index];
			if (
				child?.type === 'menu-item' &&
				isSectionOverflowItemKey(child.key) &&
				willComponentRender(child, childrenMap)
			) {
				viewMoreItem = child;
				break;
			}
		}
		const items = children.filter(
			(child): child is RegisterMenuItem =>
				child.type === 'menu-item' &&
				!isSectionOverflowItemKey(child.key) &&
				willComponentRender(child, childrenMap),
		);

		if (items.length === 0) {
			continue;
		}

		if (
			sectionOverflowLimit !== undefined &&
			viewMoreItem !== undefined &&
			items.length > sectionOverflowLimit
		) {
			sections.push([section, ...items.slice(0, sectionOverflowLimit), viewMoreItem]);
			continue;
		}

		sections.push([section, ...items]);
	}

	return { footer, root, sections };
};
